import React, { useEffect, useRef } from 'react';
import { PLAYERS_TITLE, PLAYERS_CSS, PLAYERS_HTML } from './landingAssets';
import Footer from '../../components/landing/Footer/Footer';
import Copyright from '../../components/landing/Copyright/Copyright';

// The marketing site's GlobalStyles (src/styles/Global.js) aggressively styles
// generic elements — a universal `* { color; font-family; scrollbar-width }`
// plus `h1..h6` and `p,body,a,div { font-size; line-height; font-weight }`.
// Those bleed onto this landing (which is inheritance-based) and break it
// (e.g. `currentColor` icons turn green, body text switches font, scrollbar
// hidden). This shim rolls those exact properties back with `revert` — i.e.
// "as if the app's author rules weren't there".
//
// It is scoped to `.lp-scope` (the landing markup) via `:where()`, which adds
// ZERO specificity — so the rules keep the same weight as the global versions
// (the landing's own CSS still wins, the app bleed is still cancelled) while
// matching ONLY landing elements. The shared <Footer/> + <Copyright/> below
// live OUTSIDE .lp-scope and therefore keep the app's native styling, so they
// render exactly like the rest of the marketing site.
const HOST_RESET_CSS = `
  /* .lp-scope's ancestor (#root) carries the app's *{font-family;color}, so
     inherit/revert would pull those polluted values in. Establish the landing's
     own base on the container explicitly (matching its body{} rule), then let
     descendants inherit THAT and the landing's own rules override per element. */
  .lp-scope { font-family: 'Sora', sans-serif; color: #f4f5f1; line-height: 1.55; }
  .lp-scope * { font-family: inherit; }
  :where(.lp-scope) * { color: inherit; }
  :where(.lp-scope) :is(h1, h2, h3, h4, h5, h6) { font-size: revert; line-height: revert; font-weight: revert; }
  :where(.lp-scope) :is(p, a, div, span) { font-size: revert; line-height: revert; }
  * { scrollbar-width: revert; -ms-overflow-style: revert; }
  ::-webkit-scrollbar { width: revert; height: revert; }
  *::-webkit-scrollbar-track { background: revert; border-radius: revert; }
  *::-webkit-scrollbar-thumb { background-color: revert; border: revert; border-radius: revert; }
`;

// Player PWA landing page. The markup + CSS are the sales-provided HTML,
// preserved verbatim (see landingAssets.js) so the rendered page is identical
// to the original standalone file. The original inline <script> is
// re-implemented below as a mount-scoped effect; the <style> is rendered with
// the component so it only applies while this route is mounted (no bleed into
// the rest of the marketing site).
const Players = () => {
  const containerRef = useRef(null);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = PLAYERS_TITLE;
    document.documentElement.classList.remove('no-js');

    const root = containerRef.current;
    if (!root) {
      return undefined;
    }

    // --- Pricing toggle (monthly / yearly) ---
    const monthly = root.querySelector('#tgMonthly');
    const yearly = root.querySelector('#tgYearly');
    const amt = root.querySelector('#priceAmt');
    const per = root.querySelector('#pricePer');
    const note = root.querySelector('#priceNote');
    const checkoutBtn = root.querySelector('#checkoutBtn');

    const selectMonthly = () => {
      monthly.classList.add('active');
      yearly.classList.remove('active');
      amt.textContent = '€69';
      per.textContent = '/ month';
      note.textContent = 'Billed monthly · cancel anytime';
      checkoutBtn.textContent = 'Start now — €69/mo';
    };
    const selectYearly = () => {
      yearly.classList.add('active');
      monthly.classList.remove('active');
      amt.textContent = '€690';
      per.textContent = '/ year';
      note.textContent = 'Billed yearly · 2 months free vs monthly';
      checkoutBtn.textContent = 'Start now — €690/yr';
    };

    const hasPricing = monthly && yearly && amt && per && note && checkoutBtn;
    if (hasPricing) {
      monthly.addEventListener('click', selectMonthly);
      yearly.addEventListener('click', selectYearly);
    }

    // --- Scroll reveal ---
    const reveals = Array.from(root.querySelectorAll('.reveal'));
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    reveals.forEach((el, i) => {
      // eslint-disable-next-line no-param-reassign
      el.style.transitionDelay = `${(i % 4) * 70}ms`;
      io.observe(el);
    });
    // Safety net: guarantee visibility after 4s in case the observer never fires.
    const safety = setTimeout(() => {
      reveals.forEach((el) => el.classList.add('in'));
    }, 4000);

    return () => {
      document.title = previousTitle;
      if (hasPricing) {
        monthly.removeEventListener('click', selectMonthly);
        yearly.removeEventListener('click', selectYearly);
      }
      io.disconnect();
      clearTimeout(safety);
    };
  }, []);

  return (
    <>
      {/* Cancel host-app GlobalStyles bleed first… */}
      {/* eslint-disable-next-line react/no-danger */}
      <style dangerouslySetInnerHTML={{ __html: HOST_RESET_CSS }} />
      {/* …then the verbatim landing CSS wins over the reset. */}
      {/* eslint-disable-next-line react/no-danger */}
      <style dangerouslySetInnerHTML={{ __html: PLAYERS_CSS }} />
      {/* eslint-disable-next-line react/no-danger */}
      <div className="lp-scope" ref={containerRef} dangerouslySetInnerHTML={{ __html: PLAYERS_HTML }} />
      {/* Shared site footer (correct Privacy/Terms/contact links), styled by the app. */}
      <Footer />
      <Copyright />
    </>
  );
};

export default Players;
